import useSWR, { KeyedMutator } from 'swr'
import type { AccessDenied } from 'shared/errors/access-denied'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { MEMBERSHIP_API } from 'modules/school/api/membership-api'
import type {
  CourseQuizDataInProgressInterface,
  CourseQuizDataResultInterface,
  CourseQuizId,
} from '../types/course-quiz-interface'

export interface UseCourseProps {
  courseQuizId: CourseQuizId | undefined
  shouldFetch?: boolean
  accessDeniedResponseHandler?: (error: AccessDenied) => void
}

export function useCourseQuiz({
  courseQuizId,
  shouldFetch,
  accessDeniedResponseHandler,
}: UseCourseProps): {
  courseQuizData: CourseQuizDataInProgressInterface | CourseQuizDataResultInterface | undefined
  mutate: KeyedMutator<CourseQuizDataInProgressInterface | CourseQuizDataResultInterface>
  isValidating: boolean
} {
  const { user } = useUser()
  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.get,
    CourseQuizDataInProgressInterface | CourseQuizDataResultInterface
  >({ method: RequestMethodsEnum.get, accessDeniedResponseHandler })

  const {
    data: courseQuizData,
    mutate,
    isValidating,
  } = useSWR<CourseQuizDataInProgressInterface | CourseQuizDataResultInterface>(
    () =>
      isAuthorizedUser(user) &&
      courseQuizId !== undefined &&
      shouldFetch &&
      `${MEMBERSHIP_API}/quiz/${courseQuizId}`,
    fetcher,
  )

  return { courseQuizData, mutate, isValidating }
}
